import { arrayBufferToBase64 } from "../../../utils/base64";
import {
  downloadCompanyCollectionWorkbook,
  resolveCompanyCollectionWorkbooks,
} from "../../be/endpoints/companyCollections";
import type {
  CompanyCollectionWorkbook,
  ResolveCompanyCollectionWorkbooksResponse,
} from "../../be/types";
import {
  deleteSlidesViaIntents,
  getActivePresentationRefViaIntents,
  getPreslinkCreationPresentationContextViaIntents,
  getSelectedSlideRefViaIntents,
  listSlidesViaIntents,
  writeFileFromBase64WithOutcomeViaIntents,
  type FactSetPresentationRef,
  type PreslinkCreationPresentationContext,
} from "../../../services/factset/intentBridgeService";
import {
  assertPreslinkDeckUsable,
  EXCEL_MAX_FULL_PATH_LENGTH,
  isPreslinkWorkbookWriteCollision,
  PRESLINK_WORKBOOK_NAME_ATTEMPTS,
  PreslinkPreflightError,
  type PreslinkDeckLocation,
} from "../../../services/ai/preslinkChartPreflight";
import {
  createPresentationLinks,
  PreslinkCreateLinksError,
  type PreslinkCreateLinksResponse,
} from "../../../services/ai/preslinkCreateLinksApi";
import {
  fetchLibrarySlideBase64,
  insertSlideLibraryItemsViaFactSet,
  type SlideLibraryInsertionItem,
  type SlideLibraryInsertionResult,
} from "./insertionService";
import {
  remapInsertedPreslinkBindings,
  verifyInsertedPreslinkBindings,
  type InsertedPreslinkSlideWorkbooks,
  type PreslinkBindingWorkbook,
} from "./preslinkBindingService";
import { readPreslinkTagsFromPptx } from "./preslinkPptxTags";

type Logger = (message: string) => void;

const COMPANY_WORKBOOK_RESOLVE_BATCH_SIZE = 50;
/** Smart Slide timestamps names to the second, so a retry has to start in a later second. */
const SMART_SLIDE_RETRY_DELAY_MS = 1_100;
const WINDOWS_INVALID_FILE_NAME_CHARACTERS = /[<>:"/\\|?*\u0000-\u001f]/g;
const DECK_CHANGED_MESSAGE =
  "The active presentation changed while the PresLink slides were being added. Switch back to it and try again.";

export interface PersistedPreslinkWorkbook {
  workbookId: string;
  originalFileName: string;
  localFileName: string;
  localPath: string;
  sha256: string;
}

export interface PersistedPreslinkWorkbooks {
  presentationContext: PreslinkCreationPresentationContext;
  workbookIdsBySlideId: Map<string, string[]>;
  workbooks: Map<string, PersistedPreslinkWorkbook>;
}

function normalizePresentationIdentity(value: string | undefined): string {
  return (value ?? "").trim().replace(/\\/g, "/").toLowerCase();
}

export function preslinkPresentationRefMatchesContext(
  context: PreslinkCreationPresentationContext,
  presentationRef: Partial<FactSetPresentationRef> | null | undefined
): boolean {
  const pairs = [
    [context.openPresentationId, presentationRef?.openPresentationId],
    [context.fullName, presentationRef?.fullName],
  ].filter(
    (pair): pair is [string, string] =>
      Boolean(pair[0]?.trim()) && Boolean(pair[1]?.trim())
  );
  if (pairs.length === 0) return false;
  return pairs.every(
    ([expected, actual]) =>
      normalizePresentationIdentity(expected) === normalizePresentationIdentity(actual)
  );
}

/** A selected slide with its file; `workbookIds` is empty for a slide without PresLink links. */
interface ActionSlide {
  item: SlideLibraryInsertionItem & { base64Slides: string };
  workbookIds: string[];
}

interface ActionWorkbook {
  id: string;
  originalFileName: string;
  /** The collection's file name without any folder; also the name Smart Slide renames from. */
  fileName: string;
  sha256: string;
  base64: string;
}

interface PreslinkInsertAction {
  presentationContext: PreslinkCreationPresentationContext;
  presentationRef: FactSetPresentationRef;
  location: PreslinkDeckLocation;
  slides: ActionSlide[];
  /** In first-use order across the selected slides. */
  workbooks: ActionWorkbook[];
  snapshotSlideIds: string[];
  anchorSlideId?: string;
  log?: Logger;
}

interface SmartSlideAttemptState {
  timestamp?: string;
  /** Workbook copies this action has written, by workbook ID. */
  saved: Map<string, PreslinkBindingWorkbook>;
  /** Set before the first insert, since a failed insert can still leave a slide behind. */
  insertStarted: boolean;
  insertedSlideIds: string[];
}

class SmartSlideAttemptError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "SmartSlideAttemptError";
    this.code = code;
    Object.setPrototypeOf(this, SmartSlideAttemptError.prototype);
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function basename(value: string): string {
  return value.trim().split(/[\\/]/).filter(Boolean).pop() ?? "";
}

function normalizeBase64(value: string): string {
  return value.replace(/\s+/g, "");
}

function isLinked(slide: ActionSlide): boolean {
  return slide.workbookIds.length > 0;
}

function itemLabel(item: SlideLibraryInsertionItem): string {
  return item.title?.trim() || item.id;
}

function splitAtLastDot(fileName: string): { stem: string; extension: string } {
  const dot = fileName.lastIndexOf(".");
  return dot > 0
    ? { stem: fileName.slice(0, dot), extension: fileName.slice(dot) }
    : { stem: fileName, extension: "" };
}

/** Smart Slide's naming: `<stem>_<timestamp><ext>`, split at the last dot. */
function timestampedWorkbookName(fileName: string, timestamp: string): string {
  const { stem, extension } = splitAtLastDot(fileName);
  return `${stem}_${timestamp}${extension}`;
}

/** UTC `YYYYMMDDHHMMSS`, the timestamp format Smart Slide appends to workbook names. */
function formatLinkTimestamp(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}`
  );
}

function joinDeckPath(location: PreslinkDeckLocation, fileName: string): string {
  return `${location.folderPath}${location.separator}${fileName}`;
}

function insertedSlideIdsOf(result: SlideLibraryInsertionResult): string[] {
  return result.successes.flatMap((success) =>
    success.insertedSlides.map((slide) => slide.insertedSlideId)
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mergeResolvedWorkbooks(
  target: {
    workbookIdsBySlideId: Map<string, string[]>;
    workbooksById: Map<string, CompanyCollectionWorkbook>;
  },
  response: ResolveCompanyCollectionWorkbooksResponse,
  requestedSlideIds: Set<string>
): void {
  for (const slide of response.slides) {
    const slideId = slide.slide_id.trim();
    if (!requestedSlideIds.has(slideId)) continue;
    const workbookIds = Array.from(
      new Set(slide.workbook_ids.map((workbookId) => workbookId.trim()).filter(Boolean))
    );
    target.workbookIdsBySlideId.set(slideId, workbookIds);
  }

  for (const workbook of response.workbooks) {
    const workbookId = workbook.id?.trim();
    if (workbookId) {
      target.workbooksById.set(workbookId, workbook);
    }
  }
}

/** A slide with no mapping is returned without one; a mapped workbook must have a record and a URL. */
async function resolveSelectedCollectionWorkbooks(
  collectionId: string,
  companyId: string,
  slideIds: string[]
): Promise<{
  workbookIdsBySlideId: Map<string, string[]>;
  workbooksById: Map<string, CompanyCollectionWorkbook>;
}> {
  const requestedSlideIds = new Set(slideIds);
  const resolved = {
    workbookIdsBySlideId: new Map<string, string[]>(),
    workbooksById: new Map<string, CompanyCollectionWorkbook>(),
  };

  for (
    let offset = 0;
    offset < slideIds.length;
    offset += COMPANY_WORKBOOK_RESOLVE_BATCH_SIZE
  ) {
    const response = await resolveCompanyCollectionWorkbooks(
      collectionId,
      companyId,
      slideIds.slice(offset, offset + COMPANY_WORKBOOK_RESOLVE_BATCH_SIZE)
    );
    mergeResolvedWorkbooks(resolved, response, requestedSlideIds);
  }

  for (const slideId of slideIds) {
    for (const workbookId of resolved.workbookIdsBySlideId.get(slideId) ?? []) {
      const workbook = resolved.workbooksById.get(workbookId);
      if (!workbook) {
        throw new Error(`PresLink workbook ${workbookId} was not returned by the collection API.`);
      }
      if (!workbook.url?.trim()) {
        throw new Error(`PresLink workbook ${workbook.file_name || workbookId} has no download URL.`);
      }
    }
  }

  return resolved;
}

async function classifySlides(
  items: SlideLibraryInsertionItem[],
  workbookIdsBySlideId: Map<string, string[]>,
  log?: Logger
): Promise<ActionSlide[]> {
  const slides: ActionSlide[] = [];
  for (const item of items) {
    const label = itemLabel(item);
    const base64Slides =
      item.base64Slides?.trim() || (await fetchLibrarySlideBase64(item.pptxSources, log));
    if (!base64Slides) {
      throw new Error(`Slide "${label}" could not be downloaded from the library. Nothing was inserted.`);
    }
    let tagCount: number;
    try {
      tagCount = (await readPreslinkTagsFromPptx(base64Slides)).tags.length;
    } catch {
      throw new Error(`Slide "${label}" has a PresLink link that cannot be read. Nothing was inserted.`);
    }
    const workbookIds = workbookIdsBySlideId.get(item.id.trim()) ?? [];
    if (tagCount > 0 && workbookIds.length === 0) {
      throw new Error(
        `Slide "${label}" has PresLink charts but no workbook in this collection. Nothing was inserted.`
      );
    }
    if (tagCount === 0 && workbookIds.length > 0) {
      throw new Error(
        `Slide "${label}" has a workbook in this collection but no PresLink charts. Nothing was inserted.`
      );
    }
    slides.push({ item: { ...item, base64Slides }, workbookIds });
  }
  return slides;
}

async function downloadActionWorkbooks(
  collectionId: string,
  slides: ActionSlide[],
  workbooksById: Map<string, CompanyCollectionWorkbook>
): Promise<ActionWorkbook[]> {
  const workbookIds = Array.from(new Set(slides.flatMap((slide) => slide.workbookIds)));
  const workbooks: ActionWorkbook[] = [];
  for (const workbookId of workbookIds) {
    const record = workbooksById.get(workbookId);
    if (!record) {
      throw new Error(`PresLink workbook ${workbookId} was not returned by the collection API.`);
    }
    const bytes = await downloadCompanyCollectionWorkbook(collectionId, record.url);
    if (bytes.byteLength === 0) {
      throw new Error(`PresLink workbook ${record.file_name || workbookId} was empty.`);
    }
    workbooks.push({
      id: workbookId,
      originalFileName: record.file_name,
      fileName: basename(record.file_name) || `${workbookId}.xlsx`,
      sha256: record.sha256,
      base64: arrayBufferToBase64(bytes),
    });
  }
  return workbooks;
}

/** The chart preflight's deck rules, reworded for inserting slides. */
function assertDeckAcceptsPreslinkSlides(
  context: PreslinkCreationPresentationContext
): PreslinkDeckLocation {
  try {
    return assertPreslinkDeckUsable(context);
  } catch (error) {
    if (!(error instanceof PreslinkPreflightError)) throw error;
    throw new PreslinkPreflightError(
      error.code,
      error.code === "preslink_deck_read_only"
        ? "This presentation is read-only, so PresLink slides cannot be added. Open an editable copy and try again."
        : "Save the presentation to a local or network folder before adding PresLink slides. " +
            "SharePoint, OneDrive, and other online locations are not supported."
    );
  }
}

async function captureAnchorSlideId(
  presentationRef: FactSetPresentationRef,
  log?: Logger
): Promise<string | undefined> {
  try {
    return (await getSelectedSlideRefViaIntents(log, { presentationRef })).id;
  } catch (error) {
    log?.(`Could not read the selected slide before the PresLink insert; the host default is used: ${errorMessage(error)}`);
    return undefined;
  }
}

async function assertActiveDeckUnchanged(action: PreslinkInsertAction): Promise<void> {
  const activeRef = await getActivePresentationRefViaIntents(action.log);
  if (!preslinkPresentationRefMatchesContext(action.presentationContext, activeRef)) {
    throw new Error(DECK_CHANGED_MESSAGE);
  }
}

function warnAboutLeftoverWorkbooks(action: PreslinkInsertAction, saved: Map<string, PreslinkBindingWorkbook>): void {
  if (saved.size === 0) return;
  action.log?.(
    `⚠️ PresLink workbook copies left in the presentation's folder: ${Array.from(saved.values())
      .map((workbook) => workbook.localFileName)
      .join(", ")}`
  );
}

async function requestSmartSlideLinks(action: PreslinkInsertAction): Promise<PreslinkCreateLinksResponse> {
  try {
    return await createPresentationLinks({
      folderPath: action.location.folderPath,
      presentations: action.slides.filter(isLinked).map((slide, index) => ({
        fileName: `slide-${index + 1}.pptx`,
        base64: slide.item.base64Slides,
      })),
      excelFiles: action.workbooks.map((workbook) => ({
        fileName: workbook.fileName,
        base64: workbook.base64,
      })),
    });
  } catch (error) {
    if (error instanceof PreslinkCreateLinksError) {
      throw new SmartSlideAttemptError(error.code, error.message);
    }
    throw new SmartSlideAttemptError("preslink_create_links_unreachable", "Smart Slide could not be reached.");
  }
}

/** Check Smart Slide's output against the request; returns each workbook's new name and each linked slide's file. */
async function validateSmartSlideLinks(
  action: PreslinkInsertAction,
  response: PreslinkCreateLinksResponse
): Promise<{ workbookNames: Map<string, string>; linkedSlideFiles: string[] }> {
  const unexpected = (detail: string) =>
    new SmartSlideAttemptError("preslink_create_links_unexpected_output", `Smart Slide returned ${detail}.`);

  const workbookNames = new Map<string, string>();
  const seenNames = new Set<string>();
  action.workbooks.forEach((workbook, index) => {
    const returned = response.excelFiles[index];
    const expectedName = timestampedWorkbookName(workbook.fileName, response.timestamp);
    if (returned.fileName !== expectedName) throw unexpected("an unexpected workbook name");
    if (seenNames.has(expectedName.toLowerCase())) throw unexpected("the same name for two workbooks");
    seenNames.add(expectedName.toLowerCase());
    if (joinDeckPath(action.location, expectedName).length > EXCEL_MAX_FULL_PATH_LENGTH) {
      throw new SmartSlideAttemptError(
        "preslink_create_links_path_too_long",
        `Smart Slide's workbook path is longer than Excel's ${EXCEL_MAX_FULL_PATH_LENGTH}-character limit.`
      );
    }
    if (normalizeBase64(returned.base64) !== normalizeBase64(workbook.base64)) {
      throw unexpected("changed workbook contents");
    }
    workbookNames.set(workbook.id, expectedName);
  });

  const linkedSlides = action.slides.filter(isLinked);
  const linkedSlideFiles: string[] = [];
  for (const [index, slide] of linkedSlides.entries()) {
    const returned = response.presentations[index];
    if (returned.fileName !== `slide-${index + 1}.pptx`) throw unexpected("slides out of order");
    let linkTags: Awaited<ReturnType<typeof readPreslinkTagsFromPptx>>;
    try {
      linkTags = await readPreslinkTagsFromPptx(returned.base64);
    } catch {
      throw unexpected("a slide whose links cannot be read");
    }
    if (linkTags.tags.length === 0 || linkTags.tags.length !== returned.shapesUpdated) {
      throw unexpected("a different number of linked charts than it reported");
    }
    if (linkTags.hasRootPlaceholder) throw unexpected("a slide that is still unlinked");
    const ownNames = new Set(
      slide.workbookIds.map((workbookId) => (workbookNames.get(workbookId) ?? "").toLowerCase())
    );
    for (const tag of linkTags.tags) {
      if (tag.path.replace(/[\\/]+$/, "") !== action.location.folderPath) {
        throw unexpected("a link to another folder");
      }
      if (!ownNames.has(tag.fileName.toLowerCase())) {
        throw unexpected("a link to a workbook the slide does not use");
      }
    }
    linkedSlideFiles.push(returned.base64);
  }
  return { workbookNames, linkedSlideFiles };
}

async function writeSmartSlideWorkbooks(
  action: PreslinkInsertAction,
  workbookNames: Map<string, string>,
  state: SmartSlideAttemptState
): Promise<"written" | "first_write_collided"> {
  await assertActiveDeckUnchanged(action);
  for (const [index, workbook] of action.workbooks.entries()) {
    const localFileName = workbookNames.get(workbook.id) ?? "";
    const localPath = joinDeckPath(action.location, localFileName);
    const outcome = await writeFileFromBase64WithOutcomeViaIntents({
      filePath: localPath,
      base64File: workbook.base64,
      overwrite: false,
      log: action.log,
    });
    if (outcome.status === "written") {
      state.saved.set(workbook.id, { localFileName, localPath });
      continue;
    }
    const collided = isPreslinkWorkbookWriteCollision(outcome);
    if (index === 0 && collided) return "first_write_collided";
    throw new SmartSlideAttemptError(
      collided ? "preslink_workbook_name_taken" : "preslink_workbook_write_failed",
      "A PresLink workbook could not be saved next to the presentation."
    );
  }
  return "written";
}

async function runSmartSlideAttempt(
  action: PreslinkInsertAction,
  state: SmartSlideAttemptState
): Promise<SlideLibraryInsertionResult> {
  let response = await requestSmartSlideLinks(action);
  state.timestamp = response.timestamp;
  let validated = await validateSmartSlideLinks(action, response);
  if ((await writeSmartSlideWorkbooks(action, validated.workbookNames, state)) === "first_write_collided") {
    await sleep(SMART_SLIDE_RETRY_DELAY_MS);
    const retry = await requestSmartSlideLinks(action);
    if (retry.timestamp <= response.timestamp) {
      throw new SmartSlideAttemptError(
        "preslink_create_links_stale_timestamp",
        "Smart Slide returned the same workbook names again."
      );
    }
    response = retry;
    state.timestamp = retry.timestamp;
    validated = await validateSmartSlideLinks(action, retry);
    if ((await writeSmartSlideWorkbooks(action, validated.workbookNames, state)) === "first_write_collided") {
      throw new SmartSlideAttemptError(
        "preslink_workbook_name_taken",
        "A PresLink workbook could not be saved next to the presentation."
      );
    }
  }

  await assertActiveDeckUnchanged(action);
  const linkedSlideFiles = [...validated.linkedSlideFiles];
  const slideByItem = new Map<SlideLibraryInsertionItem, ActionSlide>();
  const items = action.slides.map((slide) => {
    const item = isLinked(slide)
      ? { ...slide.item, base64Slides: linkedSlideFiles.shift() ?? "" }
      : slide.item;
    slideByItem.set(item, slide);
    return item;
  });
  state.insertStarted = true;
  const result = await insertSlideLibraryItemsViaFactSet({
    items,
    log: action.log,
    presentationRef: action.presentationRef,
    initialTargetSlideId: action.anchorSlideId,
    stopOnFirstFailure: true,
  });
  state.insertedSlideIds.push(...insertedSlideIdsOf(result));
  if (result.failures.length > 0) {
    throw new SmartSlideAttemptError("preslink_slide_insert_failed", result.failures[0].error);
  }

  const linkedInserted: InsertedPreslinkSlideWorkbooks[] = result.successes.flatMap((success) => {
    const slide = slideByItem.get(success.item);
    if (!slide || !isLinked(slide)) return [];
    const workbooks = slide.workbookIds
      .map((workbookId) => state.saved.get(workbookId))
      .filter((workbook): workbook is PreslinkBindingWorkbook => Boolean(workbook));
    return success.insertedSlides.map((inserted) => ({
      insertedSlideId: inserted.insertedSlideId,
      workbooks,
    }));
  });
  try {
    await verifyInsertedPreslinkBindings({
      slides: linkedInserted,
      presentationRef: action.presentationRef,
      log: action.log,
    });
  } catch (error) {
    throw new SmartSlideAttemptError("preslink_link_verification_failed", errorMessage(error));
  }
  return {
    ...result,
    successes: result.successes.map((success) => ({
      ...success,
      item: slideByItem.get(success.item)?.item ?? success.item,
    })),
  };
}

/**
 * Remove the slides a failed Smart Slide attempt inserted. Throws, so that the
 * fallback does not run, unless the deck is back to its slides before the action.
 */
async function rollBackSmartSlideAttempt(
  action: PreslinkInsertAction,
  state: SmartSlideAttemptState,
  attemptError: SmartSlideAttemptError
): Promise<void> {
  if (!state.insertStarted) return;
  const unsafe = (detail: string) => {
    warnAboutLeftoverWorkbooks(action, state.saved);
    return new Error(
      `PresLink slides could not be linked (${attemptError.message}), and ${detail}. Check the presentation before trying again.`
    );
  };
  if (state.insertedSlideIds.length > 0) {
    try {
      await deleteSlidesViaIntents(state.insertedSlideIds, {
        log: action.log,
        presentationRef: action.presentationRef,
      });
    } catch (error) {
      throw unsafe(`removing the slides added so far failed: ${errorMessage(error)}`);
    }
  }
  let remainingSlideIds: string[];
  try {
    remainingSlideIds = (
      await listSlidesViaIntents(action.log, { forceRefresh: true, presentationRef: action.presentationRef })
    ).map((slide) => slide.slideId);
  } catch (error) {
    throw unsafe(`the presentation's slides could not be read back: ${errorMessage(error)}`);
  }
  const restored =
    remainingSlideIds.length === action.snapshotSlideIds.length &&
    remainingSlideIds.every((slideId, index) => slideId === action.snapshotSlideIds[index]);
  if (!restored) {
    throw unsafe("the presentation's slides changed during the insert, so it was not retried");
  }
}

/** `<stem>_<timestamp>[_<copy>]<ext>`, made safe for Windows and shortened to Excel's path limit. */
function buildFallbackWorkbookName(
  location: PreslinkDeckLocation,
  sourceFileName: string,
  timestamp: string,
  copy: number
): string {
  const { stem, extension } = splitAtLastDot(
    basename(sourceFileName).replace(WINDOWS_INVALID_FILE_NAME_CHARACTERS, "_")
  );
  const suffix = `_${timestamp}${copy > 1 ? `_${copy}` : ""}${extension}`;
  const stemBudget =
    EXCEL_MAX_FULL_PATH_LENGTH - `${location.folderPath}${location.separator}`.length - suffix.length;
  if (stemBudget < 1) {
    throw new PreslinkPreflightError(
      "preslink_path_too_long",
      `The presentation's folder path is too long for Excel: even the shortest workbook name would exceed ${EXCEL_MAX_FULL_PATH_LENGTH} characters. Move the presentation to a shorter folder path.`
    );
  }
  const safeStem = stem.replace(/[. ]+$/, "") || "Workbook";
  const shortened = safeStem.slice(0, stemBudget);
  return `${shortened.replace(/[ ._-]+$/, "") || shortened}${suffix}`;
}

async function writeFallbackWorkbook(
  action: PreslinkInsertAction,
  workbook: ActionWorkbook,
  timestamp: string,
  usedNames: Set<string>
): Promise<PreslinkBindingWorkbook> {
  let attempts = 0;
  for (let copy = 1; attempts < PRESLINK_WORKBOOK_NAME_ATTEMPTS; copy += 1) {
    const localFileName = buildFallbackWorkbookName(action.location, workbook.fileName, timestamp, copy);
    if (usedNames.has(localFileName.toLowerCase())) continue;
    usedNames.add(localFileName.toLowerCase());
    attempts += 1;
    const localPath = joinDeckPath(action.location, localFileName);
    const outcome = await writeFileFromBase64WithOutcomeViaIntents({
      filePath: localPath,
      base64File: workbook.base64,
      overwrite: false,
      log: action.log,
    });
    if (outcome.status === "written") return { localFileName, localPath };
    if (!isPreslinkWorkbookWriteCollision(outcome)) {
      throw new Error(`Unable to save PresLink workbook ${localFileName}: ${outcome.message}`);
    }
  }
  throw new Error(
    `Unable to save PresLink workbook ${workbook.fileName}: the names tried are already taken in the presentation's folder.`
  );
}

/** #940's insert-then-rewrite path, with the same timestamped names as the Smart Slide path. */
async function runFallback(
  action: PreslinkInsertAction,
  state: SmartSlideAttemptState
): Promise<SlideLibraryInsertionResult> {
  try {
    await assertActiveDeckUnchanged(action);
  } catch (error) {
    warnAboutLeftoverWorkbooks(action, state.saved);
    throw error;
  }
  const timestamp = state.timestamp ?? formatLinkTimestamp(new Date());
  const usedNames = new Set(
    Array.from(state.saved.values()).map((workbook) => workbook.localFileName.toLowerCase())
  );
  const persisted = new Map<string, PersistedPreslinkWorkbook>();
  try {
    for (const workbook of action.workbooks) {
      const local =
        state.saved.get(workbook.id) ??
        (await writeFallbackWorkbook(action, workbook, timestamp, usedNames));
      state.saved.set(workbook.id, local);
      persisted.set(workbook.id, {
        workbookId: workbook.id,
        originalFileName: workbook.originalFileName,
        localFileName: local.localFileName,
        localPath: local.localPath,
        sha256: workbook.sha256,
      });
    }
  } catch (error) {
    warnAboutLeftoverWorkbooks(action, state.saved);
    throw error;
  }

  const result = await insertSlideLibraryItemsViaFactSet({
    items: action.slides.map((slide) => slide.item),
    log: action.log,
    presentationRef: action.presentationRef,
    initialTargetSlideId: action.anchorSlideId,
  });
  const linkedItems = new Set<SlideLibraryInsertionItem>(
    action.slides.filter(isLinked).map((slide) => slide.item)
  );
  try {
    await remapInsertedPreslinkBindings({
      insertionResult: {
        ...result,
        successes: result.successes.filter((success) => linkedItems.has(success.item)),
      },
      persisted: {
        presentationContext: action.presentationContext,
        workbookIdsBySlideId: new Map(
          action.slides.map((slide) => [slide.item.id, slide.workbookIds] as const)
        ),
        workbooks: persisted,
      },
      presentationRef: action.presentationRef,
      log: action.log,
    });
  } catch (error) {
    let rollbackNote = "";
    const insertedSlideIds = insertedSlideIdsOf(result);
    if (insertedSlideIds.length > 0) {
      try {
        await deleteSlidesViaIntents(insertedSlideIds, {
          log: action.log,
          presentationRef: action.presentationRef,
        });
        rollbackNote = " The inserted slides were removed.";
      } catch (deleteError) {
        rollbackNote = ` Removing the inserted slides also failed: ${errorMessage(deleteError)}`;
      }
    }
    warnAboutLeftoverWorkbooks(action, state.saved);
    throw new Error(
      `PresLink slides could not be linked to their workbooks: ${errorMessage(error)}${rollbackNote}`
    );
  }
  return result;
}

/**
 * Insert slides from a PresLink company collection. Smart Slide relinks the slides
 * to timestamped workbook copies next to the deck; if that fails before the deck
 * keeps any change, the original slides are inserted and relinked in place.
 * Returns the insertion result, or throws an error the user can read.
 */
export async function insertPreslinkCollectionSlides(input: {
  collectionId: string;
  companyId: string;
  items: SlideLibraryInsertionItem[];
  log?: Logger;
}): Promise<SlideLibraryInsertionResult> {
  const log = input.log;
  const collectionId = input.collectionId.trim();
  const companyId = input.companyId.trim();
  const slideIds = Array.from(
    new Set(input.items.map((item) => item.id.trim()).filter(Boolean))
  );
  if (!collectionId || !companyId || slideIds.length === 0) {
    throw new Error("PresLink insertion requires a collection, a company, and at least one slide.");
  }

  const resolved = await resolveSelectedCollectionWorkbooks(collectionId, companyId, slideIds);
  const slides = await classifySlides(input.items, resolved.workbookIdsBySlideId, log);
  if (!slides.some(isLinked)) {
    log?.("PresLink insert: no selected slide has PresLink links; inserting them as standard slides.");
    let presentationRef: FactSetPresentationRef | null = null;
    try {
      presentationRef = await getActivePresentationRefViaIntents(log);
    } catch (error) {
      log?.(`Could not capture the target presentation before slide-library insertion: ${errorMessage(error)}`);
    }
    return insertSlideLibraryItemsViaFactSet({
      items: slides.map((slide) => slide.item),
      log,
      presentationRef,
    });
  }

  const presentationContext = await getPreslinkCreationPresentationContextViaIntents(log);
  const location = assertDeckAcceptsPreslinkSlides(presentationContext);
  const presentationRef = await getActivePresentationRefViaIntents(log);
  if (!presentationRef || !preslinkPresentationRefMatchesContext(presentationContext, presentationRef)) {
    throw new Error(DECK_CHANGED_MESSAGE);
  }
  const workbooks = await downloadActionWorkbooks(collectionId, slides, resolved.workbooksById);
  const snapshotSlideIds = (
    await listSlidesViaIntents(log, { forceRefresh: true, presentationRef })
  ).map((slide) => slide.slideId);
  const action: PreslinkInsertAction = {
    presentationContext,
    presentationRef,
    location,
    slides,
    workbooks,
    snapshotSlideIds,
    anchorSlideId: await captureAnchorSlideId(presentationRef, log),
    log,
  };

  const state: SmartSlideAttemptState = {
    saved: new Map(),
    insertStarted: false,
    insertedSlideIds: [],
  };
  try {
    const result = await runSmartSlideAttempt(action, state);
    log?.("PresLink insert: linked through Smart Slide.");
    return result;
  } catch (error) {
    const attemptError =
      error instanceof SmartSlideAttemptError
        ? error
        : new SmartSlideAttemptError("preslink_smart_slide_attempt_failed", errorMessage(error));
    log?.(`PresLink insert: the Smart Slide path failed (${attemptError.code}); using the fallback.`);
    await rollBackSmartSlideAttempt(action, state, attemptError);
  }

  const result = await runFallback(action, state);
  log?.("PresLink insert: linked through the fallback.");
  return result;
}
